import React, { useEffect, useMemo, useState } from 'react';
import { getDefaultApiHostname, normalizeApiHostname } from '../api/client';

interface DocsPortalProps {
  apiKeyAvailable: boolean;
}

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';
type DocsTab = 'start' | 'api' | 'resources';

interface OpenApiOperation {
  summary?: string;
  description?: string;
  operationId?: string;
  tags?: string[];
  parameters?: unknown[];
  requestBody?: unknown;
  responses?: Record<string, unknown>;
}

interface OpenApiSpec {
  openapi?: string;
  info?: {
    title?: string;
    version?: string;
    description?: string;
  };
  paths?: Record<string, Partial<Record<HttpMethod, OpenApiOperation>>>;
}

interface EndpointRow {
  method: HttpMethod;
  path: string;
  summary: string;
  tag: string;
  description: string;
  operation: OpenApiOperation;
  hasParameters: boolean;
  hasRequestBody: boolean;
  responseCodes: string[];
}

const methodOrder: HttpMethod[] = ['get', 'post', 'put', 'patch', 'delete'];
const methodLabels: Record<HttpMethod, string> = {
  get: 'GET',
  post: 'POST',
  put: 'PUT',
  patch: 'PATCH',
  delete: 'DELETE',
};

type AnyRecord = Record<string, any>;

const isRecord = (value: unknown): value is AnyRecord =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const getContent = (value: unknown): Record<string, AnyRecord> => {
  if (!isRecord(value) || !isRecord(value.content)) {
    return {};
  }
  return value.content;
};

const getSchema = (value: unknown): AnyRecord | null => {
  if (!isRecord(value)) return null;
  if (isRecord(value.schema)) return value.schema;
  return null;
};

const summarizeSchema = (schema: unknown): string => {
  if (!isRecord(schema)) return 'value';
  if (typeof schema.$ref === 'string') return schema.$ref.split('/').pop() || schema.$ref;
  if (schema.type === 'array') return `array<${summarizeSchema(schema.items)}>`;
  if (schema.type === 'object' || isRecord(schema.properties)) {
    const keys = Object.keys(schema.properties || {});
    return keys.length ? `object: ${keys.slice(0, 6).join(', ')}${keys.length > 6 ? ', ...' : ''}` : 'object';
  }
  return schema.type || schema.format || 'value';
};

const exampleFromSchema = (schema: unknown, depth = 0): unknown => {
  if (!isRecord(schema) || depth > 4) return null;
  if (Array.isArray(schema.example)) return schema.example;
  if (schema.example !== undefined) return schema.example;
  if (Array.isArray(schema.enum) && schema.enum.length > 0) return schema.enum[0];
  if (typeof schema.$ref === 'string') return `<${schema.$ref.split('/').pop() || 'ref'}>`;

  if (schema.type === 'array') {
    return [exampleFromSchema(schema.items, depth + 1)];
  }

  if (schema.type === 'object' || isRecord(schema.properties)) {
    const properties = isRecord(schema.properties) ? schema.properties : {};
    return Object.entries(properties).reduce<AnyRecord>((acc, [key, value]) => {
      acc[key] = exampleFromSchema(value, depth + 1);
      return acc;
    }, {});
  }

  if (schema.type === 'integer' || schema.type === 'number') return 0;
  if (schema.type === 'boolean') return true;
  if (schema.format === 'date-time') return '2026-05-28T00:00:00Z';
  if (schema.format === 'date') return '2026-05-28';
  return 'string';
};

const getRequestBodyExample = (endpoint: EndpointRow): string | null => {
  const content = getContent(endpoint.operation.requestBody);
  const jsonMedia = content['application/json'] || Object.values(content)[0];
  const schema = getSchema(jsonMedia);
  if (!schema) return null;
  return JSON.stringify(exampleFromSchema(schema), null, 2);
};

const DocsPortal: React.FC<DocsPortalProps> = ({ apiKeyAvailable }) => {
  const [copied, setCopied] = useState<string | null>(null);
  const [spec, setSpec] = useState<OpenApiSpec | null>(null);
  const [specError, setSpecError] = useState<string | null>(null);
  const [specLoading, setSpecLoading] = useState(false);
  const [endpointQuery, setEndpointQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState('all');
  const [selectedMethod, setSelectedMethod] = useState<'all' | HttpMethod>('all');
  const [activeDocsTab, setActiveDocsTab] = useState<DocsTab>('start');

  const apiHost = normalizeApiHostname(localStorage.getItem('apiHostname') || getDefaultApiHostname());
  const apiKey = localStorage.getItem('apiKey') || '';
  const swaggerUrl = `${apiHost}/api/doc`;
  const openApiUrl = `${apiHost}/api/doc.json`;
  const openApiCurl = `curl -H "X-API-Key: ${apiKey || '<provided-api-key>'}" "${openApiUrl}"`;

  const bootstrapPrompt = useMemo(() => {
    return `You are helping configure Form Pipeline for this website.

Backend API host:
${apiHost}

OpenAPI JSON:
${openApiUrl}

Swagger UI:
${swaggerUrl}

Authentication:
Send the API key in every API request using this header:
X-API-Key: <provided-api-key>

Start by reading the OpenAPI spec, then:
1. List existing forms, notification channels, and API capabilities before creating anything.
2. Create or update forms for the website's inbound lead/contact flows.
3. Configure allowed origins for the website domains that will submit forms.
4. Create or reuse notification/integration channels such as email, Slack, CRM, webhook, SMS, or marketing automation.
5. Attach channels to the appropriate forms for approved, flagged, and spam outcomes.
6. Submit a test payload to each form endpoint and inspect submissions/results.
7. Report the form endpoint URLs, channel routing, and any secrets or DNS/CORS requirements that still need human action.

Do not assume backend defaults. Verify each resource with the API before changing it.`;
  }, [apiHost, openApiUrl, swaggerUrl]);

  const endpoints = useMemo<EndpointRow[]>(() => {
    if (!spec?.paths) {
      return [];
    }

    return Object.entries(spec.paths).flatMap(([path, operations]) => (
      methodOrder.flatMap((method) => {
        const operation = operations[method];
        if (!operation) {
          return [];
        }

        return [{
          method,
          path,
          summary: operation.summary || operation.operationId || `${methodLabels[method]} ${path}`,
          tag: operation.tags?.[0] || 'General',
          description: operation.description || '',
          operation,
          hasParameters: Boolean(operation.parameters?.length),
          hasRequestBody: Boolean(operation.requestBody),
          responseCodes: Object.keys(operation.responses || {}),
        }];
      })
    )).sort((a, b) => `${a.tag}:${a.path}:${a.method}`.localeCompare(`${b.tag}:${b.path}:${b.method}`));
  }, [spec]);

  const tags = useMemo(() => (
    Array.from(new Set(endpoints.map(endpoint => endpoint.tag))).sort()
  ), [endpoints]);

  const filteredEndpoints = useMemo(() => {
    const query = endpointQuery.trim().toLowerCase();
    return endpoints.filter((endpoint) => {
      const tagMatches = selectedTag === 'all' || endpoint.tag === selectedTag;
      const methodMatches = selectedMethod === 'all' || endpoint.method === selectedMethod;
      if (!tagMatches || !methodMatches) {
        return false;
      }
      if (!query) {
        return true;
      }
      return [
        endpoint.method,
        endpoint.path,
        endpoint.summary,
        endpoint.tag,
        endpoint.description,
      ].some(value => value.toLowerCase().includes(query));
    });
  }, [endpointQuery, endpoints, selectedMethod, selectedTag]);

  const apiSummary = useMemo(() => {
    const methods = endpoints.reduce((acc, endpoint) => {
      acc[endpoint.method] = (acc[endpoint.method] ?? 0) + 1;
      return acc;
    }, {} as Partial<Record<HttpMethod, number>>);
    return {
      endpoints: endpoints.length,
      sections: tags.length,
      methods,
    };
  }, [endpoints, tags.length]);

  useEffect(() => {
    if (!apiKeyAvailable || !apiKey) {
      return;
    }

    let cancelled = false;

    const loadSpec = async () => {
      setSpecLoading(true);
      setSpecError(null);

      try {
        const response = await fetch(openApiUrl, {
          headers: {
            'X-API-Key': apiKey,
          },
        });

        if (!response.ok) {
          throw new Error(`OpenAPI request failed with HTTP ${response.status}`);
        }

        const data = await response.json();
        if (!cancelled) {
          setSpec(data);
        }
      } catch (error) {
        if (!cancelled) {
          setSpec(null);
          setSpecError(error instanceof Error ? error.message : 'Unable to load OpenAPI spec');
        }
      } finally {
        if (!cancelled) {
          setSpecLoading(false);
        }
      }
    };

    loadSpec();

    return () => {
      cancelled = true;
    };
  }, [apiKey, apiKeyAvailable, openApiUrl]);

  const copyText = async (key: string, value: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(key);
    window.setTimeout(() => setCopied(null), 1800);
  };

  const openSwaggerWithKey = async () => {
    if (apiKey) {
      await navigator.clipboard.writeText(apiKey);
      setCopied('swagger-key');
      window.setTimeout(() => setCopied(null), 1800);
    }
    window.open(swaggerUrl, '_blank', 'noopener,noreferrer');
  };

  const buildEndpointCurl = (endpoint: EndpointRow) => {
    const exampleBody = getRequestBodyExample(endpoint);
    const lines = [
      `curl -X ${methodLabels[endpoint.method]} \\`,
      `  -H "X-API-Key: ${apiKey || '<provided-api-key>'}" \\`,
    ];

    if (endpoint.hasRequestBody) {
      lines.push('  -H "Content-Type: application/json" \\');
      lines.push(`  -d '${exampleBody || '{}'}' \\`);
    }

    lines.push(`  "${apiHost}/api${endpoint.path}"`);
    return lines.join('\n');
  };

  return (
    <div className="docs-page">
      {!apiKeyAvailable && (
        <div className="alert alert-info">
          <span>Info</span>
          <span>Configure an API key first. The docs endpoints are protected by the backend.</span>
        </div>
      )}

      <div className="docs-page-header list-page-header">
        <div className="docs-title-row list-page-title">
          <h2>Docs</h2>
          <span className={`docs-version-badge ${!spec && !specLoading ? 'is-hidden' : ''}`}>
            {specLoading ? 'Loading API' : spec?.info?.version ? `API v${spec.info.version}` : 'OpenAPI'}
          </span>
        </div>
        <div className="docs-header-actions list-page-actions">
          <button className="btn btn-secondary" onClick={() => copyText('bootstrap-header', bootstrapPrompt)}>
            {copied === 'bootstrap-header' ? 'Copied' : 'Copy AI Bootstrap'}
          </button>
          <button className="btn btn-secondary" onClick={openSwaggerWithKey}>
            {copied === 'swagger-key' ? 'Key Copied' : 'Open Swagger'}
          </button>
        </div>
      </div>

      <section className="docs-shell-card">
        <div className="local-tabs docs-tabs" role="tablist" aria-label="Docs sections">
          <div className="local-tabs-primary">
            <button
              className={activeDocsTab === 'start' ? 'active' : ''}
              onClick={() => setActiveDocsTab('start')}
              role="tab"
              aria-selected={activeDocsTab === 'start'}
            >
              Start Here
            </button>
            <button
              className={activeDocsTab === 'api' ? 'active' : ''}
              onClick={() => setActiveDocsTab('api')}
              role="tab"
              aria-selected={activeDocsTab === 'api'}
            >
              API Browser
            </button>
            <button
              className={activeDocsTab === 'resources' ? 'active' : ''}
              onClick={() => setActiveDocsTab('resources')}
              role="tab"
              aria-selected={activeDocsTab === 'resources'}
            >
              Links & Auth
            </button>
          </div>
        </div>

        {activeDocsTab === 'start' && (
          <div className="docs-tab-panel docs-scroll-panel">
            <section className="docs-grid docs-grid-start">
              <div className="docs-card docs-checklist">
                <p className="docs-label">Recommended workflow</p>
                <h3>Setup sequence</h3>
                <ol>
                  <li>Inspect existing forms, channels, and permissions.</li>
                  <li>Create or update website forms and allowed origins.</li>
                  <li>Wire notification channels for approved, flagged, and spam outcomes.</li>
                  <li>Submit test payloads and report endpoint URLs.</li>
                </ol>
              </div>
            </section>

            <section className="docs-card">
              <div className="card-header">
                <h3>Agent Setup One-Pager</h3>
                <button className="btn btn-sm btn-primary" onClick={() => copyText('bootstrap-secondary', bootstrapPrompt)}>
                  {copied === 'bootstrap-secondary' ? 'Copied' : 'Copy'}
                </button>
              </div>
              <pre className="docs-prompt">{bootstrapPrompt}</pre>
            </section>
          </div>
        )}

        {activeDocsTab === 'api' && (
          <div className="docs-tab-panel docs-api-panel">
            {specError && (
              <div className="alert alert-error">
                <span>Error</span>
                <span>{specError}</span>
              </div>
            )}

            {!specError && !apiKeyAvailable && (
              <div className="empty-state">
                <h3>API key required</h3>
                <p>The OpenAPI browser can load after the SPA has an API key.</p>
              </div>
            )}

            {!specError && apiKeyAvailable && (
              <>
                <div className="docs-browser-controls">
                  <div className="docs-browser-toolbar">
                    <input
                      value={endpointQuery}
                      onChange={(event) => setEndpointQuery(event.target.value)}
                      placeholder="Search endpoints..."
                    />
                    <select value={selectedTag} onChange={(event) => setSelectedTag(event.target.value)}>
                      <option value="all">All sections</option>
                      {tags.map(tag => (
                        <option key={tag} value={tag}>{tag}</option>
                      ))}
                    </select>
                    <select value={selectedMethod} onChange={(event) => setSelectedMethod(event.target.value as 'all' | HttpMethod)}>
                      <option value="all">All methods</option>
                      {methodOrder
                        .filter(method => (apiSummary.methods[method] ?? 0) > 0)
                        .map(method => (
                          <option key={method} value={method}>
                            {methodLabels[method]} ({apiSummary.methods[method]})
                          </option>
                        ))}
                    </select>
                    <button className="btn btn-secondary" onClick={() => copyText('spec-json', JSON.stringify(spec, null, 2))} disabled={!spec}>
                      {copied === 'spec-json' ? 'Copied' : 'Copy loaded JSON'}
                    </button>
                  </div>
                  <p className="docs-result-count">
                    Showing {filteredEndpoints.length} of {apiSummary.endpoints} endpoints across {apiSummary.sections} sections.
                  </p>
                </div>

                <div className="docs-endpoint-list">
                  {filteredEndpoints.map(endpoint => {
                    const requestExample = getRequestBodyExample(endpoint);
                    return (
                      <article className="docs-endpoint" key={`${endpoint.method}:${endpoint.path}`}>
                        <div className="docs-endpoint-main">
                          <span className={`method-badge method-${endpoint.method}`}>{methodLabels[endpoint.method]}</span>
                          <div>
                            <h4>{endpoint.summary}</h4>
                            <code>{endpoint.path}</code>
                            {endpoint.description && <p>{endpoint.description}</p>}
                            <div className="docs-endpoint-flags">
                              {endpoint.operation.operationId && <span>{endpoint.operation.operationId}</span>}
                              {endpoint.hasParameters && <span>Parameters</span>}
                              {endpoint.hasRequestBody && <span>Request body</span>}
                            </div>
                          </div>
                        </div>
                        <div className="docs-endpoint-meta">
                          <span>{endpoint.tag}</span>
                          {endpoint.responseCodes.length > 0 && <small>{endpoint.responseCodes.join(', ')}</small>}
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => copyText(`url:${endpoint.method}:${endpoint.path}`, `${apiHost}/api${endpoint.path}`)}
                          >
                            {copied === `url:${endpoint.method}:${endpoint.path}` ? 'Copied' : 'Copy URL'}
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => copyText(`${endpoint.method}:${endpoint.path}`, buildEndpointCurl(endpoint))}
                          >
                            {copied === `${endpoint.method}:${endpoint.path}` ? 'Copied' : 'Copy curl'}
                          </button>
                          {requestExample && (
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => copyText(`body:${endpoint.method}:${endpoint.path}`, requestExample)}
                            >
                              {copied === `body:${endpoint.method}:${endpoint.path}` ? 'Copied' : 'Copy body'}
                            </button>
                          )}
                        </div>
                        {(endpoint.hasParameters || endpoint.hasRequestBody || endpoint.responseCodes.length > 0) && (
                          <details className="docs-endpoint-details">
                            <summary>Details</summary>
                            <SpecDetails endpoint={endpoint} requestExample={requestExample} />
                          </details>
                        )}
                      </article>
                    );
                  })}
                </div>

                {spec && filteredEndpoints.length === 0 && (
                  <div className="empty-state">
                    <h3>No endpoints match</h3>
                    <p>Try a broader search or switch sections.</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeDocsTab === 'resources' && (
          <div className="docs-tab-panel docs-scroll-panel">
            <section className="docs-grid docs-grid-secondary">
              <div className="docs-card">
                <p className="docs-label">API explorer</p>
                <h3>Swagger UI</h3>
                <p>Human-readable endpoint explorer. Open it, then authorize with your API key.</p>
                <div className="docs-url-row">
                  <code>{swaggerUrl}</code>
                  <button className="btn btn-sm btn-secondary" onClick={() => copyText('swagger', swaggerUrl)}>
                    {copied === 'swagger' ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <button className="btn btn-secondary docs-link-button" onClick={openSwaggerWithKey}>
                  {copied === 'swagger-key' ? 'API key copied' : 'Open and copy API key'}
                </button>
                <p className="docs-note">Swagger opens in a new tab. The SPA copies your API key first so you can paste it into Authorize.</p>
              </div>

              <div className="docs-card">
                <p className="docs-label">Machine-readable spec</p>
                <h3>OpenAPI JSON</h3>
                <p>Use this URL for AI tools, SDK generators, API clients, and documentation tooling.</p>
                <div className="docs-url-row">
                  <code>{openApiUrl}</code>
                  <button className="btn btn-sm btn-secondary" onClick={() => copyText('openapi', openApiUrl)}>
                    {copied === 'openapi' ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <button className="btn btn-secondary docs-link-button" onClick={() => copyText('openapi-curl', openApiCurl)}>
                  {copied === 'openapi-curl' ? 'Copied' : 'Copy authenticated curl'}
                </button>
                {spec && (
                  <button className="btn btn-secondary docs-link-button" onClick={() => copyText('loaded-openapi-json', JSON.stringify(spec, null, 2))}>
                    {copied === 'loaded-openapi-json' ? 'Copied' : 'Copy loaded JSON'}
                  </button>
                )}
              </div>

              <InfoCard
                title="Backend target"
                body={`This SPA is currently pointed at ${apiHost}. Switch backend hosts from setup/reset when working with another tenant.`}
              />
            </section>

            <section className="docs-grid docs-grid-secondary docs-resource-notes">
              <InfoCard
                title="Authentication"
                body="Both Swagger UI and OpenAPI JSON require an API key with api_keys.view permission. This SPA loads OpenAPI JSON with your stored key and copies that key before opening Swagger."
              />
              <InfoCard
                title="For humans"
                body="Use Swagger UI when you want to browse and test endpoints interactively from the backend documentation surface."
              />
              <InfoCard
                title="For agents"
                body="Use the OpenAPI JSON URL and the bootstrap prompt when giving an AI enough context to work against a tenant."
              />
            </section>
          </div>
        )}
      </section>
    </div>
  );
};

function InfoCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="card docs-card">
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

function SpecDetails({ endpoint, requestExample }: { endpoint: EndpointRow; requestExample: string | null }) {
  return (
    <div className="docs-detail-grid">
      {endpoint.hasParameters && <ParametersDetail parameters={endpoint.operation.parameters} />}
      {endpoint.hasRequestBody && <RequestBodyDetail requestBody={endpoint.operation.requestBody} requestExample={requestExample} />}
      {endpoint.responseCodes.length > 0 && <ResponsesDetail responses={endpoint.operation.responses} />}
    </div>
  );
}

function ParametersDetail({ parameters }: { parameters: unknown }) {
  const rows = Array.isArray(parameters) ? parameters : [];
  return (
    <div className="docs-detail-section">
      <h5>Parameters</h5>
      {rows.length > 0 ? (
        <table className="docs-mini-table">
          <tbody>
            {rows.map((param, index) => {
              const record = isRecord(param) ? param : {};
              return (
                <tr key={`${record.name || 'param'}-${index}`}>
                  <td>{record.name || '-'}</td>
                  <td>{record.in || '-'}</td>
                  <td>{record.required ? 'required' : 'optional'}</td>
                  <td>{summarizeSchema(record.schema)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p>No parameters documented.</p>
      )}
    </div>
  );
}

function RequestBodyDetail({ requestBody, requestExample }: { requestBody: unknown; requestExample: string | null }) {
  const content = getContent(requestBody);
  const contentTypes = Object.keys(content);
  return (
    <div className="docs-detail-section">
      <h5>Request body</h5>
      {contentTypes.length > 0 ? (
        <>
          <ul className="docs-schema-list">
            {contentTypes.map((type) => (
              <li key={type}>
                <strong>{type}</strong>
                <span>{summarizeSchema(getSchema(content[type]))}</span>
              </li>
            ))}
          </ul>
          {requestExample && <pre>{requestExample}</pre>}
        </>
      ) : (
        <p>Request body is documented without content metadata.</p>
      )}
    </div>
  );
}

function ResponsesDetail({ responses }: { responses: unknown }) {
  const rows = isRecord(responses) ? Object.entries(responses) : [];
  return (
    <div className="docs-detail-section">
      <h5>Responses</h5>
      {rows.length > 0 ? (
        <table className="docs-mini-table">
          <tbody>
            {rows.map(([code, response]) => {
              const record = isRecord(response) ? response : {};
              const contentTypes = Object.keys(getContent(record));
              return (
                <tr key={code}>
                  <td>{code}</td>
                  <td>{record.description || '-'}</td>
                  <td>{contentTypes.length ? contentTypes.join(', ') : '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p>No response metadata documented.</p>
      )}
    </div>
  );
}

export default DocsPortal;
